import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { paths } from '../../routes/paths';
import { ReportDrilldowns } from '../reports/ReportDrilldowns';
import { CredentialsPanel } from './CredentialsPanel';
import { IpAllowlistPanel } from './IpAllowlistPanel';
import { PartnerFormDialog } from './PartnerFormDialog';
import { toPageError } from './PartnerListPage';
import { PartnerStatusDialog } from './PartnerStatusDialog';
import { partnerRepositoryToken } from './repository';
import type { PartnerRepository } from './repository';
import type { PartnerDetail } from './types';

type PartnerTab = 'overview' | 'credentials' | 'ip';

export function PartnerDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { partnerId = '' } = useParams();
  const repository = useRepository(partnerRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState<PartnerTab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.get(partnerId, signal),
    [repository, partnerId, refreshKey],
    () => false,
  );
  const principal = auth.status === 'authenticated' ? auth.principal : null;
  const canManage = principal?.permissions.includes('PARTNER_MANAGE') === true;
  const canManageCredentials = principal?.permissions.includes('CREDENTIAL_MANAGE') === true;
  const canViewGames = principal?.permissions.includes('GAME_VIEW') === true;
  const canViewWithdrawalControls = principal?.permissions.includes('WITHDRAWAL_VIEW') === true;
  const canManageIntegration = principal?.permissions.includes('INTEGRATION_MANAGE') === true;
  const refresh = () => setRefreshKey((key) => key + 1);

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toPageError(resource.error, t('partners.errors.detail'))}
        onRetry={refresh}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <PartnerDetailContent
            canManage={canManage}
            canManageCredentials={canManageCredentials}
            canManageIntegration={canManageIntegration}
            canViewGames={canViewGames}
            canViewWithdrawalControls={canViewWithdrawalControls}
            dataSource={dataSource}
            language={language}
            onEdit={() => setEditOpen(true)}
            onStatus={() => setStatusOpen(true)}
            onTab={setTab}
            partner={resource.data}
            repository={repository}
            tab={tab}
          />
        )}
      </PageState>
      {resource.data === null ? null : (
        <>
          <PartnerFormDialog
            onClose={() => setEditOpen(false)}
            onSaved={refresh}
            open={editOpen}
            partner={resource.data}
            repository={repository}
          />
          <PartnerStatusDialog
            onClose={() => setStatusOpen(false)}
            onSaved={refresh}
            open={statusOpen}
            partner={resource.data}
            repository={repository}
          />
        </>
      )}
    </Box>
  );
}

function PartnerDetailContent({
  canManage,
  canManageCredentials,
  canManageIntegration,
  canViewGames,
  canViewWithdrawalControls,
  dataSource,
  language,
  onEdit,
  onStatus,
  onTab,
  partner,
  repository,
  tab,
}: {
  readonly canManage: boolean;
  readonly canManageCredentials: boolean;
  readonly canManageIntegration: boolean;
  readonly canViewGames: boolean;
  readonly canViewWithdrawalControls: boolean;
  readonly dataSource: DataSource;
  readonly language: TimeLanguage;
  readonly onEdit: () => void;
  readonly onStatus: () => void;
  readonly onTab: (tab: PartnerTab) => void;
  readonly partner: PartnerDetail;
  readonly repository: PartnerRepository;
  readonly tab: PartnerTab;
}) {
  const { t } = useTranslation();
  return (
    <>
      <DetailHeader
        actions={
          canManage ? (
            <>
              <Button onClick={onEdit} variant="outlined">
                {t('partners.actions.edit')}
              </Button>
              <Button
                color="warning"
                disabled={partner.status === 'CLOSED'}
                onClick={onStatus}
                variant="contained"
              >
                {t('partners.actions.changeStatus')}
              </Button>
            </>
          ) : undefined
        }
        backTo={paths.partners}
        dataSource={dataSource}
        subtitle={partner.name}
        title={partner.code}
      />
      <Tabs
        aria-label={t('partners.detail.tabsLabel')}
        onChange={(_event, value: PartnerTab) => onTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        value={tab}
        variant="scrollable"
      >
        <Tab label={t('partners.detail.overview')} value="overview" />
        {canManageCredentials ? (
          <Tab label={t('partners.credentials.title')} value="credentials" />
        ) : null}
        {canManageCredentials ? <Tab label={t('partners.ip.title')} value="ip" /> : null}
      </Tabs>
      {tab === 'overview' ? (
        <PartnerOverview
          canViewGames={canViewGames}
          canViewWithdrawalControls={canViewWithdrawalControls}
          canManageIntegration={canManageIntegration}
          language={language}
          partner={partner}
        />
      ) : null}
      {tab === 'credentials' && canManageCredentials ? (
        <CredentialsPanel language={language} partnerId={partner.id} repository={repository} />
      ) : null}
      {tab === 'ip' && canManageCredentials ? (
        <IpAllowlistPanel partnerId={partner.id} repository={repository} />
      ) : null}
    </>
  );
}

function PartnerOverview({
  canViewGames,
  canViewWithdrawalControls,
  canManageIntegration,
  language,
  partner,
}: {
  readonly canViewGames: boolean;
  readonly canViewWithdrawalControls: boolean;
  readonly canManageIntegration: boolean;
  readonly language: TimeLanguage;
  readonly partner: PartnerDetail;
}) {
  const { t } = useTranslation();
  const created = formatTimestamp(partner.createdAt, 'Asia/Bangkok', language);
  const updated = formatTimestamp(partner.updatedAt, 'Asia/Bangkok', language);
  return (
    <Stack spacing={2}>
      <ReportDrilldowns
        metrics={[
          {
            kind: 'transactions',
            filters: { partnerId: partner.id },
            label: t('reportSummaries.metrics.partnerRequests'),
            value: null,
          },
          {
            kind: 'audit-activity',
            filters: { partnerId: partner.id },
            label: t('reportSummaries.metrics.partnerActivity'),
            value: null,
          },
        ]}
      />
      <Paper component="section" variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
          {t('partners.detail.overview')}
        </Typography>
        <MetadataList
          items={[
            { label: t('partners.fields.name'), value: partner.name },
            { label: t('partners.fields.status'), value: <StatusChip status={partner.status} /> },
            { label: t('partners.fields.currency'), value: partner.defaultCurrencyCode },
            { label: t('partners.fields.timezone'), value: partner.timezone },
            {
              label: t('partners.fields.affiliateCommissionExpiry'),
              value:
                partner.affiliateCommissionExpiryDays === null
                  ? t('partners.detail.neverExpires')
                  : t('partners.detail.expiryDays', {
                      count: partner.affiliateCommissionExpiryDays,
                    }),
            },
            {
              label: t('partners.fields.usernameNormalization'),
              value: partner.usernameNormalizationMode,
            },
            {
              label: t('partners.fields.requestRateLimit'),
              value: partner.requestRateLimitPerMinute,
            },
            {
              label: t('partners.fields.withdrawalRateLimit'),
              value: partner.withdrawalRateLimitPerMinute,
            },
            {
              label: t('partners.fields.timestampTolerance'),
              value: partner.timestampToleranceSeconds,
            },
            {
              label: t('partners.fields.createdAt'),
              value: `${created.display} (${created.originalOffset})`,
            },
            {
              label: t('partners.fields.updatedAt'),
              value: `${updated.display} (${updated.originalOffset})`,
            },
          ]}
        />
      </Paper>
      <Paper
        component="nav"
        aria-label={t('partners.detail.related')}
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Typography component="h2" variant="h6">
          {t('partners.detail.related')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          {canViewGames ? (
            <MuiLink component={Link} to={`${paths.partners}/${partner.id}/games`}>
              {t('partners.detail.gameAvailability')}
            </MuiLink>
          ) : null}
          {canViewWithdrawalControls ? (
            <MuiLink
              component={Link}
              to={`${paths.partners}/${encodeURIComponent(partner.id)}/withdrawal-controls`}
            >
              {t('partners.detail.withdrawalControls')}
            </MuiLink>
          ) : null}
          {canManageIntegration ? (
            <MuiLink
              component={Link}
              to={`${paths.partnerCredit}/${encodeURIComponent(partner.id)}`}
            >
              {t('partners.detail.partnerCredit')}
            </MuiLink>
          ) : null}
          <MuiLink component={Link} to={paths.audit}>
            {t('partners.detail.audit')}
          </MuiLink>
        </Stack>
      </Paper>
    </Stack>
  );
}

function StatusChip({ status }: { readonly status: PartnerDetail['status'] }) {
  return (
    <Chip
      color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}
      label={status}
      size="small"
    />
  );
}
